import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Mail, Newspaper, Settings, FolderOpen,
  Users, Handshake, MessageSquareQuote, Sun, FileCheck, Scale, Globe,
  Image, LogOut, ChevronLeft, Menu, ArrowLeft, Linkedin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { authAPI } from '../services/api';

// Import admin section components
import DashboardOverview from '../components/admin/DashboardOverview';
import SiteSettingsEditor from '../components/admin/SiteSettingsEditor';
import HeroEditor from '../components/admin/HeroEditor';
import AboutEditor from '../components/admin/AboutEditor';
import ServicesPillarsEditor from '../components/admin/ServicesPillarsEditor';
import RenewableServicesEditor from '../components/admin/RenewableServicesEditor';
import RegulatoryServicesEditor from '../components/admin/RegulatoryServicesEditor';
import LegalServicesEditor from '../components/admin/LegalServicesEditor';
import TestimonialsEditor from '../components/admin/TestimonialsEditor';
import ContactsManager from '../components/admin/ContactsManager';
import NewslettersManager from '../components/admin/NewslettersManager';
import BlogManager from '../components/admin/BlogManager';
import ProjectsManager from '../components/admin/ProjectsManager';
import TeamManager from '../components/admin/TeamManager';
import PartnersManager from '../components/admin/PartnersManager';

import HomepageSectionsEditor from '../components/admin/HomepageSectionsEditor';
import LinkedInPostsManager from '../components/admin/LinkedInPostsManager';

const navSections = [
  { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { type: 'divider', label: 'Website Content' },
  { id: 'site-settings', label: 'Site Settings', icon: Settings },
  { id: 'hero', label: 'Hero Section', icon: Image },
  { id: 'homepage-sections', label: 'Homepage Sections', icon: Globe },
  { id: 'about', label: 'About Page', icon: Globe },
  { type: 'divider', label: 'Services' },
  { id: 'services-pillars', label: 'Service Pillars', icon: Sun },
  { id: 'renewable', label: 'Renewable Services', icon: Sun },
  { id: 'regulatory', label: 'Regulatory Services', icon: FileCheck },
  { id: 'legal', label: 'Legal Services', icon: Scale },
  { type: 'divider', label: 'Data Management' },
  { id: 'projects', label: 'Projects', icon: FolderOpen },
  { id: 'team', label: 'Team Members', icon: Users },
  { id: 'partners', label: 'Partners & Customers', icon: Handshake },
  { id: 'testimonials', label: 'Testimonials', icon: MessageSquareQuote },
  { type: 'divider', label: 'Communications' },
  { id: 'blog', label: 'Blog Posts', icon: FileText },
  { id: 'linkedin', label: 'LinkedIn Posts', icon: Linkedin },
  { id: 'contacts', label: 'Contact Messages', icon: Mail },
  { id: 'newsletters', label: 'Newsletter Subs', icon: Newspaper },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const res = await authAPI.getCurrentUser();
      setCurrentUser(res.data);
    } catch {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    loadUser();
  }, [navigate, loadUser]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out');
    navigate('/admin/login');
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'overview': return <DashboardOverview />;
      case 'site-settings': return <SiteSettingsEditor />;
      case 'hero': return <HeroEditor />;
      case 'homepage-sections': return <HomepageSectionsEditor />;
      case 'about': return <AboutEditor />;
      case 'services-pillars': return <ServicesPillarsEditor />;
      case 'renewable': return <RenewableServicesEditor />;
      case 'regulatory': return <RegulatoryServicesEditor />;
      case 'legal': return <LegalServicesEditor />;
      case 'projects': return <ProjectsManager />;
      case 'team': return <TeamManager />;
      case 'partners': return <PartnersManager />;
      case 'testimonials': return <TestimonialsEditor />;
      case 'blog': return <BlogManager navigate={navigate} />;
      case 'linkedin': return <LinkedInPostsManager />;
      case 'contacts': return <ContactsManager />;
      case 'newsletters': return <NewslettersManager />;
      default: return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'} bg-white border-r border-gray-200 flex flex-col transition-all duration-200 fixed h-full z-20`}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-emerald-700">SOLARIAN CMS</h2>
          <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-gray-600">
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-2" data-testid="admin-sidebar">
          {navSections.map((item, idx) => {
            if (item.type === 'divider') {
              return <p key={`divider-${item.label}`} className="px-4 pt-4 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">{item.label}</p>;
            }
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                data-testid={`nav-${item.id}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeSection === item.id
                    ? 'bg-emerald-50 text-emerald-700 border-r-2 border-emerald-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-gray-100 space-y-2">
          <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> View Website
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-0'} transition-all duration-200`}>
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            {!sidebarOpen && (
              <button onClick={() => setSidebarOpen(true)} className="text-gray-500 hover:text-gray-700">
                <Menu className="w-5 h-5" />
              </button>
            )}
            <h1 className="text-xl font-semibold text-gray-900">
              {navSections.find(s => s.id === activeSection)?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{currentUser?.email}</span>
          </div>
        </header>
        <div className="p-6">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
